/**
 * MFA / account-security contracts — TW-3455 (architecture §5, auth hardening).
 *
 * Better Auth's two-factor (TOTP) and passkey (WebAuthn) plugins own the ceremonies and their
 * tables; the client talks to them directly. What lives here is the small, framework-free
 * surface the API and the web app share: the input schemas the account-security forms validate
 * with, a pure parser for the `otpauth://` enrolment URI (so the UI can show a manual-entry
 * secret beside the QR code without a QR/OTP dependency), and a clock-free fold that turns the
 * raw factor state into the at-a-glance status both shells render.
 *
 * Everything here is pure and deterministic — no I/O, no Better Auth imports — so it is unit
 * testable exactly like the trip/manifest/messaging summaries.
 */
import { z } from "zod";

/** TOTP codes are 6 numeric digits (the default period/digits of the two-factor plugin). */
export const TOTP_CODE_LENGTH = 6;

/** A 6-digit TOTP code, entered at enrolment confirmation and at every later sign-in challenge. */
export const totpCodeSchema = z.object({
  code: z
    .string()
    .trim()
    .regex(new RegExp(`^\\d{${TOTP_CODE_LENGTH}}$`), `Enter the ${TOTP_CODE_LENGTH}-digit code`),
});
export type TotpCodeInput = z.infer<typeof totpCodeSchema>;

/**
 * A backup recovery code. Better Auth formats them as two `xxxxx-xxxxx` groups, but a user may
 * paste with or without the hyphen/spacing, so we only require a non-trivial token and let the
 * server verify the exact value.
 */
export const backupCodeSchema = z.object({
  code: z.string().trim().min(8, "Enter a backup code").max(40),
});
export type BackupCodeInput = z.infer<typeof backupCodeSchema>;

/** The parts of an `otpauth://totp/...` enrolment URI the UI needs to show a manual-entry key. */
export interface ParsedOtpAuthUri {
  /** The Base32 shared secret (uppercase, no padding) the user types into an authenticator app. */
  secret: string;
  /** The issuer/brand label (e.g. "Travel Connection Spain"), if present. */
  issuer: string | null;
  /** The account label (typically the user's email), if present. */
  account: string | null;
}

/**
 * Parse an `otpauth://totp/<label>?secret=...&issuer=...` URI into its display parts. Returns
 * null for anything that is not a TOTP otpauth URI with a secret. Pure — no QR/OTP library.
 *
 * The label is `Issuer:account` (issuer optionally repeated as a query param); both are
 * percent-encoded. We prefer the explicit `issuer` query param when present.
 */
export function parseOtpAuthUri(uri: string): ParsedOtpAuthUri | null {
  let url: URL;
  try {
    url = new URL(uri);
  } catch {
    return null;
  }
  // Protocol comes back as "otpauth:"; the host is the type ("totp" or "hotp").
  if (url.protocol !== "otpauth:" || url.host !== "totp") return null;

  const secret = (url.searchParams.get("secret") ?? "").trim().toUpperCase();
  if (!secret) return null;

  // Strip the leading "/" then split the "Issuer:account" label.
  const label = decodeURIComponent(url.pathname.replace(/^\//, ""));
  const colon = label.indexOf(":");
  const labelIssuer = colon >= 0 ? label.slice(0, colon).trim() : null;
  const account = (colon >= 0 ? label.slice(colon + 1) : label).trim() || null;

  const queryIssuer = url.searchParams.get("issuer");
  const issuer = (queryIssuer ?? labelIssuer)?.trim() || null;

  return { secret, issuer, account };
}

/** Group a Base32 secret into space-separated quads, the conventional manual-entry layout. */
export function formatTotpSecret(secret: string): string {
  return (secret.match(/.{1,4}/g) ?? []).join(" ");
}

/** A passkey credential as surfaced to the account-security UI (a thin view of Better Auth's row). */
export interface PasskeyDto {
  id: string;
  /** User-chosen label, or null if none was given at registration. */
  name: string | null;
  createdAt: string;
}

/** The factor state the account-security page reduces to its summary banner. */
export interface SecurityState {
  twoFactorEnabled: boolean;
  passkeyCount: number;
}

/** A clock-free, at-a-glance summary of which factors protect the account. */
export interface SecuritySummary extends SecurityState {
  /** True once at least one factor beyond the password is in place. */
  hasSecondFactor: boolean;
  /** i18n key describing the overall posture — drives the status pill in both shells. */
  statusKey: "secured" | "passwordOnly";
}

export function summarizeSecurity(state: SecurityState): SecuritySummary {
  const hasSecondFactor = state.twoFactorEnabled || state.passkeyCount > 0;
  return {
    ...state,
    hasSecondFactor,
    statusKey: hasSecondFactor ? "secured" : "passwordOnly",
  };
}
